import InventoriesApiV2 from 'api/v2/admins/inventories-api-v2'
import OutboundInternalRequestApiV2 from 'api/v2/admins/outbound-internal-requests-api-v2'
import TitlePage from 'components/atoms/title-page'
import { ALERT_TYPES } from 'constants/alert-constants'
import InternalRequestBorrowConfirmModalForm from 'domains/internal-request/organisms/borrow-confirm-modal-form'
import InternalRequestBorrowForm from 'domains/internal-request/organisms/borrow-form'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useState } from 'react'
import { Card } from 'reactstrap'


const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

const InternalRequestRequestorNewCreatePage = ({ pageUtils }) => {
  const [ inventories, setInventories ]                           = useState([])
  const [ selectedCurrentInventory, setSelectedCurrentInventory ] = useState({})
  const [ selectedInventories, setSelectedInventories ]           = useState([])
  const [ pagination, setPagination ]                             = useState({})
  const [ isLoading, setIsLoading ]                               = useState(true)
  const [ isOpen, setIsOpen ]                                     = useState(false)

  /** Toggle to display/hide a modal */
  const handleToggleModal = () => setIsOpen((prev) => !prev)

  /**
   * Handler to Fetch Inventories
   * @param {object} tableState - React-table final state
   */
  const handleFetchInventories = (tableState) => {
    setIsLoading(true)

    InventoriesApiV2.get({ tableState, additionalFilters: { eligible_for_internal_request: true } })
      .then((response) => {
        setInventories(response.data.inventories)
        setPagination(response.data.meta)
        setIsLoading(false)
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        setIsLoading(false)
      })
  }

  /**
   * Handle selected Inventory.
   * Add selected Inventory if it does not exist in the form
   * @param {object} datum - Inventory record
   */
  const handleSelectBorrowTable = (datum) => {
    const isAlreadyExist = selectedInventories.find((inventory) => inventory.id === datum.id)

    // Skip the selected Inventory if the Inventory has been selected before
    if (isAlreadyExist) {
      pageUtils.setAlertMsg('Inventory sudah ada di cart request', ALERT_TYPES.WARNING)
      return
    }

    setIsOpen(true)
    setSelectedCurrentInventory(datum)
  }

  /** Disable borrow button if stored quantity is not available */
  const isDisabledItem = useCallback((datum) => {
    const storedQuantity = Number(datum?.stored_quantity || datum?.quantity || 0)

    return storedQuantity <= 0
  }, [])

  /**
   * Handler to add borrowed Inventory
   * @param {object} values - Field values
   * @param {object} formikBag - Formikbag
   */
  const handleAddBorrowedInventory = (values, formikBag) => {
    formikBag.setSubmitting(false)

    const isAlreadyExist = selectedInventories.find((inventory) => inventory.id === selectedCurrentInventory.id)

    // Skip the selected Inventory if the Inventory has been selected before
    if (isAlreadyExist) {
      pageUtils.setAlertMsg('Inventory sudah ada di cart request', ALERT_TYPES.WARNING)
      formikBag.resetForm()
      setIsOpen(false)
      return
    }

    // Adding current selected Inventory with borrow qty
    setSelectedInventories([ ...selectedInventories, {
      id            : selectedCurrentInventory.id,
      inventory_sku : selectedCurrentInventory.inventory_sku,
      product_name  : selectedCurrentInventory.product_name,
      product_size  : selectedCurrentInventory.product_size,
      quantity      : values.quantity
    } ])

    pageUtils.setAlertMsg('Adding product inventory to borrow cart', ALERT_TYPES.SUCCESS)
    formikBag.resetForm()
    setIsOpen(false)
  }

  const handleSubmit = async (values, formikBag) => {

    await OutboundInternalRequestApiV2.create(values)
      .then(() => {

        pageUtils.setAlertMsg('Product has bee requested to borrow', ALERT_TYPES.SUCCESS)

        setSelectedInventories([])
        handleFetchInventories()
        formikBag.resetForm()
      })
      .catch((error) => {
        pageUtils.setApiErrorMsg(error.response.data)
        formikBag.setErrors(error.response.data.messages)
      })
  }

  /**
   * Remove selected Inventory state.
   * @param {number} index - selected Inventory record
   */
  const handleRemoveRow = useCallback((index) => {
    const newInventories = [ ...selectedInventories ]
    newInventories.splice(index, 1)

    setSelectedInventories(newInventories)
    pageUtils.setAlertMsg('Remove product inventory to borrow cart', ALERT_TYPES.SUCCESS)
  }, [ pageUtils, selectedInventories ])


  /** Data Table props */
  const dataTableProps = {
    data          : inventories,
    pagination    : pagination,
    onFetchData   : handleFetchInventories,
    isLoading     : isLoading,
    hiddenColumns : [
      'product_status',
      'location',
      'sublocation',
      'quantity',
      'inventory_type'
    ],
    rowButtonProps: {
      buttonText       : 'Borrow',
      buttonColour     : 'primary',
      isButtonDisabled : isDisabledItem,
      onButtonClick    : handleSelectBorrowTable
    }
  }

  /** Selected Borrow Inventory Product Table props */
  const selectedBorrowTableProps = useMemo(
    () => ({
      selectedInventories : selectedInventories,
      onRemoveRow         : handleRemoveRow
    }), [ selectedInventories, handleRemoveRow ])

  /** Modal props  */
  const modalProps = {
    isOpen : isOpen,
    toggle : handleToggleModal
  }

  return (
    <>
      <TitlePage mainTitle='Internal Request' subTitle='New Create Page' />

      <Card body>
        <InternalRequestBorrowForm
          dataTableProps={ dataTableProps }
          selectedBorrowTableProps={ selectedBorrowTableProps }
          setSelectedInventories={ setSelectedInventories }
          handleSubmit={ handleSubmit }
          pageUtils={ pageUtils }
        />
      </Card>

      <InternalRequestBorrowConfirmModalForm
        modalProps={ modalProps }
        selectedCurrentInventory={ selectedCurrentInventory }
        onAddBorrowedInventory={ handleAddBorrowedInventory }
      />
    </>
  )
}

InternalRequestRequestorNewCreatePage.propTypes = propTypes

export default InternalRequestRequestorNewCreatePage
